import React, { FC, useCallback, useRef, useState } from 'react';
import { Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter } from 'reactstrap';
import { SecondFactorType } from '../../../../shared/user/password';
import { MaterialIcon } from '../../../components';
import { useTranslation } from '../../../translations';
import { useConstantSetters, useInputState } from '../../../utils';

interface SecondFactorState {
  readonly onCancel: () => void;
  readonly onSubmit: () => void;
}

export const SecondFactorPrompt: FC<SecondFactorState> = ({ onCancel, onSubmit }) => {
  const { ts } = useTranslation();
  const [type, setType] = useState(SecondFactorType.Otp);
  const [secondFactorCode, setSecondFactorCode] = useInputState('');
  const secondFactor = useRef<HTMLInputElement>(null);

  const isOtp = type === SecondFactorType.Otp;
  const length = isOtp ? 6 : 10;

  const focusSecondFactor = useCallback(() => {
    secondFactor.current?.focus();
  }, []);

  const toggle2FAType = useConstantSetters(
    setType,
    isOtp ? SecondFactorType.Backup : SecondFactorType.Otp,
  );

  const onSubmitForm = useCallback(
    (e: React.UIEvent) => {
      e.preventDefault();
      onSubmit();
    },
    [onSubmit],
  );

  const onKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        onSubmit();
      }
    },
    [onSubmit],
  );

  return (
    <>
      <Input defaultValue={type} id="secondFactorType" name="secondFactor[type]" type="hidden" />
      <Input name="secondFactor[code]" required type="hidden" value={secondFactorCode} />
      <Modal isOpen onOpened={focusSecondFactor}>
        <div>
          <ModalBody className="twofa-modal">
            <div className="text-center">
              <MaterialIcon large name="security" type="primary" />
              <h4>{ts('twofactor_authentication')}</h4>
            </div>
            <hr />
            <p className="text-secondary text-center">
              {ts(isOtp ? 'enter_the_6digit_code_generated' : 'enter_one_of_your_alphanumeric')}
            </p>
            <FormGroup className="text-center">
              <Label for="secondFactor[code]">{ts('second_factor_code')}</Label>
              <Input
                autoFocus
                id="secondFactor"
                innerRef={secondFactor}
                maxLength={length}
                minLength={length}
                onChange={setSecondFactorCode}
                onKeyUp={onKeyUp}
                placeholder={isOtp ? '123456' : 'ADSKJBKASF'}
                required
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button block color="link" id="secondFactorChangeType" onClick={toggle2FAType}>
              {isOtp ? ts('use_backup_code_instead') : `Use ${ts('second_factor_code')}`}
            </Button>
            <Button color="primary" id="login2FA" onClick={onSubmitForm}>
              {ts('common.login')}
            </Button>
            <Button color="secondary" onClick={onCancel} outline>
              {ts('common.cancel')}
            </Button>
          </ModalFooter>
        </div>
      </Modal>
    </>
  );
};
